import { useModalAction } from "@/contexts/modal/Context";
import client from "@/data/client";
import { useAvailabilities, useMe } from "@/data/user";
import DashboardSideBar from "@/layouts/DashboardSidebar.tsx";
import AvailabilitiesReminder from "@/overlays/AvailabilitiesReminder";
import UpsellingMobile from "@/pages/mobile-modals/UpsellingMobile";
import { UpsellingProp, UserProducts } from "@/types";
import getLastWeekMonday from "@/utils/get-last-week-monday";
import { useBreakpoint } from "@/utils/hooks/useBreakpoints";
import useGlobals from "@/utils/hooks/useGlobals.ts";
import { fadeInBottom } from "@/utils/motion/fade-in-bottom";
import { cn } from "@edusogno/lib/utils";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";

export default function DashboardLayout() {
  const { me } = useMe();
  const location = useLocation();
  const user = me?.item.user;
  const breakpoint = useBreakpoint();
  const { openModal } = useModalAction();
  const { availabilities } = useAvailabilities();
  const { backgroundClass, showSideBar } = useGlobals();

  const isSmall = ["xs", "sm", "md"].indexOf(breakpoint) !== -1;

  const [upsellingVisible, setUpsellingVisible] = useState(false);

  const { data } = useQuery<UserProducts>(
    ["user-products"],
    client.users.products,
    {
      enabled: !location.pathname.includes("pot"),
    },
  );

  const handleUpsellingModal = (status: UpsellingProp) => {
    if (user?.crm_upselling?.meeting_date || status.status == "PERMA_CLOSED") {
      return;
    } else if (
      status.inLastWeek &&
      status.status == "NONE" &&
      (status.shownDate == null ||
        status.shownDate != dayjs().format("YYYY-MM-DD"))
    ) {
      setUpsellingVisible(true);
      if (!isSmall) {
        openModal("UPSELLING_MODAL");
      }
    } else if (
      status.inLastWeek &&
      status.status == "TEMP_CLOSED" &&
      dayjs().diff(dayjs(status.startDate), "day") % 2 == 0 &&
      status.shownDate != dayjs().format("YYYY-MM-DD")
    ) {
      status.shownDate = dayjs().format("YYYY-MM-DD");
      localStorage.setItem("upsellingStatus", JSON.stringify(true));
      setUpsellingVisible(true);
      if (!isSmall) {
        openModal("UPSELLING_MODAL");
      }
    }
  };

  useEffect(() => {
    const upSellingStatus: UpsellingProp = {
      status: "PERMA_CLOSED",
      endDate: null,
      productId: 0,
      startDate: null,
      shownDate: null,
      inLastWeek: false,
    };

    if (data?.items) {
      const currentCoursePivot = data?.items[0]?.pivot;
      const lastWeekStartDate = dayjs(
        getLastWeekMonday(currentCoursePivot?.end_date as string),
      );
      const lastWeekEndDate = dayjs(currentCoursePivot?.end_date);
      upSellingStatus.inLastWeek = dayjs().isBetween(
        lastWeekStartDate,
        lastWeekEndDate,
      );
      upSellingStatus.startDate = lastWeekStartDate.format("YYYY-MM-DD");
      upSellingStatus.endDate = lastWeekEndDate.format("YYYY-MM-DD");
      upSellingStatus.status = "NONE";
      upSellingStatus.shownDate = null;
      upSellingStatus.productId = currentCoursePivot?.product_id;

      try {
        const storedUpsellingStatus = JSON.parse(
          localStorage.getItem("upsellingStatus") as string,
        ) as UpsellingProp;
        if (
          storedUpsellingStatus.productId != data?.items?.[0]?.pivot.product_id
        ) {
          localStorage.setItem(
            "upsellingStatus",
            JSON.stringify(upSellingStatus),
          );
          handleUpsellingModal(upSellingStatus);
        } else {
          handleUpsellingModal(storedUpsellingStatus);
        }
      } catch (error) {
        localStorage.setItem(
          "upsellingStatus",
          JSON.stringify(upSellingStatus),
        );
        handleUpsellingModal(upSellingStatus);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  document.documentElement.style.setProperty(
    "--vh",
    `${window.innerHeight * 0.01}px`,
  );

  useEffect(() => {
    const handleResize = () => {
      document.documentElement.style.setProperty(
        "--vh",
        `${window.innerHeight * 0.01}px`,
      );
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const hideSidebar =
    (location.pathname.startsWith("/front/dashboard/exercises/") ||
      location?.pathname.includes("file")) &&
    !location?.pathname.includes("book-mock-test") &&
    !location?.pathname.includes("book-live");

  return (
    <>
      {isSmall && upsellingVisible && (
        <UpsellingMobile
          handleClose={() => setUpsellingVisible(false)}
          isDiscounted={true}
        />
      )}

      {Boolean(me?.item?.hasSpeaking) && !availabilities?.item?.isSuspended && (
        <AvailabilitiesReminder />
      )}

      <motion.div
        exit="exit"
        initial="exit"
        animate="enter"
        className={cn(
          "flex min-height-[-webkit-fill-available] min-h-[calc(100*var(--vh,1svh))] h-[calc(100*var(--vh,1svh))] w-full flex-col",
        )}
      >
        <motion.div
          variants={fadeInBottom()}
          className={cn(
            "flex flex-col relative flex-1 md:flex-row justify-between min-h-full h-full",
            backgroundClass,
          )}
        >
          {!hideSidebar && showSideBar && <DashboardSideBar />}

          <AnimatePresence
            mode="wait"
            initial={true}
            onExitComplete={() => window.scrollTo(0, 0)}
          >
            <Outlet />
          </AnimatePresence>
        </motion.div>
      </motion.div>
    </>
  );
}
